import { readPermission } from './modules';

// Los módulos configurados prevalecen sobre los valores históricos por rol.
// Mantener estos defaults alineados con firestore.rules y authorization.js.
export const hasOrderPermission = (user, moduleName, action = 'Edición') => {
  if (!user?.uid || (Object.prototype.hasOwnProperty.call(user, 'isActive') && user.isActive !== true)) return false;
  if (user.email === 'programacionsebastian94@gmail.com') return true;
  if (Object.prototype.hasOwnProperty.call(user.permissions || {}, moduleName)) {
    return readPermission(user.permissions[moduleName], action);
  }
  const role = String(user.role || '').trim().toLowerCase();
  if (['ti', 'administrador', 'admin'].includes(role)) return true;
  if (action === 'Eliminación') return false;
  if (role === 'supervisor') return ['Punto de Venta', 'Toma de Pedidos'].includes(moduleName) ||
    (action === 'Lectura' && moduleName === 'Panel de Despacho');
  if (role === 'cajero') return moduleName === 'Punto de Venta';
  if (role === 'mesero') return moduleName === 'Toma de Pedidos' ||
    (action === 'Lectura' && moduleName === 'Panel de Despacho');
  return false;
};

export const canChargeDispatchOrder = (user) =>
  ['Panel de Despacho', 'Toma de Pedidos', 'Punto de Venta'].some((moduleName) => hasOrderPermission(user, moduleName));

export const canPrintDispatchOrder = (user) => canChargeDispatchOrder(user) ||
  ['Panel de Despacho', 'Toma de Pedidos', 'Punto de Venta'].some((moduleName) => hasOrderPermission(user, moduleName, 'Lectura'));

export const canUseSharedDispatchCash = (user) => canChargeDispatchOrder(user) && (
  hasOrderPermission(user, 'Turnos de Caja (Admin)') ||
  String(user.role || '').trim().toLowerCase() === 'cajero' // Excepción vigente de assertCashAllowed.
);

const ownsOrder = (user, order) => Object.prototype.hasOwnProperty.call(order, 'waiterUid')
  ? order.waiterUid === user?.uid
  : [user?.username, user?.email, user?.displayName].filter(Boolean).includes(order.waiterName || order.waiter);

export const canEditDispatchContents = (user, order = {}) =>
  hasOrderPermission(user, 'Panel de Despacho') || hasOrderPermission(user, 'Punto de Venta') ||
  (hasOrderPermission(user, 'Toma de Pedidos') && ownsOrder(user, order));

export const DISPATCH_STAGE = Object.freeze({
  NEW: 'Nuevo',
  PREPARING: 'En Preparacion',
  DELIVERED: 'Entregado',
});

export const ORDER_TABLES = Object.freeze([
  ...Array.from({ length: 10 }, (_, i) => `Mesa ${i + 1}`), 'Barra', 'Para Llevar',
]);

const normalize = (value = '') =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

export const getDispatchStage = (order = {}) => {
  const explicit = normalize(order.dispatchStatus);
  if (explicit === 'nuevo') return DISPATCH_STAGE.NEW;
  if (explicit === 'entregado') return DISPATCH_STAGE.DELIVERED;
  if (explicit === 'en preparacion' || explicit === 'preparando') {
    return DISPATCH_STAGE.PREPARING;
  }

  // Compatibilidad con pedidos creados antes de introducir dispatchStatus.
  if (order.deliveredAt || normalize(order.deliveryStatus) === 'entregado') {
    return DISPATCH_STAGE.DELIVERED;
  }
  return DISPATCH_STAGE.PREPARING;
};

export const isPaymentPending = (order = {}) => normalize(order.status) !== 'cobrado';

export const dispatchStagePriority = (order = {}) => {
  const stage = getDispatchStage(order);
  if (stage === DISPATCH_STAGE.NEW) return 0;
  if (stage === DISPATCH_STAGE.PREPARING) return 1;
  return 2;
};

export const canManageDispatchOrder = (user) => canChargeDispatchOrder(user);

export const canDeleteDispatchOrder = (user, order = {}) =>
  hasOrderPermission(user, 'Panel de Despacho', 'Eliminación') ||
  hasOrderPermission(user, 'Punto de Venta', 'Eliminación') ||
  (hasOrderPermission(user, 'Toma de Pedidos', 'Eliminación') && ownsOrder(user, order));

export const canChangeOrderTable = (user, order = {}) => {
  if (!user?.uid || user.isActive === false) return false;
  if (!['En Preparacion', 'Listo para Cobro'].includes(order.status)) return false;
  const role = normalize(user.role);
  if (['ti', 'administrador', 'admin'].includes(role)) return true;
  const permissions = user.permissions?.['Panel de Despacho'];
  const canRead = permissions ? permissions.Lectura === true : ['supervisor', 'mesero'].includes(role);
  if (!canRead) return false;
  if (permissions?.['Cambiar mesa'] === true) return true;
  const ownsOrder = order.waiterUid
    ? order.waiterUid === user.uid
    : [user.username, user.email, user.displayName].filter(Boolean).includes(order.waiterName || order.waiter);
  const waiterPermissions = user.permissions?.['Toma de Pedidos'];
  const canEdit = waiterPermissions
    ? (waiterPermissions['Edición'] ?? waiterPermissions.Edicion ?? waiterPermissions['EdiciÓn']) === true
    : role === 'mesero';
  return role === 'mesero' && ownsOrder && canEdit;
};

export const validateTableChange = (user, order, expectedTable, nextTable) => {
  if (!canChangeOrderTable(user, order)) throw new Error('El pedido ya no está disponible para cambiar de mesa o no tienes permiso.');
  if ((order.table || '') !== expectedTable) throw new Error('Otro usuario cambió la mesa. Cierra esta ventana y vuelve a intentarlo.');
  if (!ORDER_TABLES.includes(nextTable)) throw new Error('Selecciona una mesa válida.');
  if (nextTable === expectedTable) throw new Error('Selecciona una mesa diferente a la actual.');
};
